/**
 * PotActionsMenu Molecule
 * Context menu for pot actions in the approval list
 * Pattern: Atomic Design - Molecules
 */

import { useState, type ReactNode } from 'react';
import {
  Button,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
} from '@billda/design-system';
import {
  MoreHorizontalIcon,
  FastForwardIcon,
  GitMergeIcon,
  CalendarClockIcon,
} from 'lucide-react';
import { Permissions, usePermission } from '@billda/permissions-ui';
import type { PotStatus } from '../types/pot.types';

export interface PotActionsMenuProps {
  potId: string;
  status: PotStatus;
  isPrimary: boolean;
  submitDeadlineExtendedUntil?: string | null;
  onMoveToActivePeriod?: (potId: string) => void;
  onMoveToPrimary?: (potId: string) => void;
  onExtendDeadline?: (potId: string) => void;
  disabled?: boolean;
  fallback?: ReactNode;
  'data-testid'?: string;
}

/**
 * Context menu for pot row actions
 * Shows available actions based on pot status and user permissions
 */
export function PotActionsMenu({
  potId,
  status,
  isPrimary,
  submitDeadlineExtendedUntil,
  onMoveToActivePeriod,
  onMoveToPrimary,
  onExtendDeadline,
  disabled,
  fallback,
  'data-testid': dataTestId,
}: PotActionsMenuProps) {
  const [open, setOpen] = useState(false);
  const canMove = usePermission(Permissions.Pots.Move);
  const canExtend = usePermission(Permissions.Pots.Extend);

  // Determine which actions are available based on status
  const canMoveToActivePeriod =
    canMove &&
    onMoveToActivePeriod !== undefined &&
    (status === 'MissedSubmitDeadline' ||
      status === 'MissedVerificationDeadline');
  const canMoveToPrimary =
    canMove &&
    onMoveToPrimary !== undefined &&
    !isPrimary &&
    status !== 'MissedSubmitDeadline' &&
    status !== 'MissedVerificationDeadline' &&
    !submitDeadlineExtendedUntil;
  const canExtendDeadline =
    canExtend &&
    onExtendDeadline !== undefined &&
    (status === 'MissedSubmitDeadline' || submitDeadlineExtendedUntil != null);
  const hasAnyAction =
    canMoveToActivePeriod || canMoveToPrimary || canExtendDeadline;

  if (!hasAnyAction) {
    return fallback ?? null;
  }

  return (
    <Menu open={open} onOpenChange={setOpen}>
      <MenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-7 shrink-0 transition-all duration-200"
          onClick={event => {
            event.stopPropagation();
          }}
          disabled={disabled || !hasAnyAction}
          aria-label="More actions"
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          data-testid={dataTestId}
        >
          <MoreHorizontalIcon aria-hidden="true" size={18} />
        </Button>
      </MenuTrigger>

      <MenuContent align="end" onClick={e => e.stopPropagation()}>
        {canMoveToActivePeriod && (
          <MenuItem
            onSelect={() => {
              onMoveToActivePeriod?.(potId);
            }}
            data-testid="move-to-active-period-menu-item"
          >
            <FastForwardIcon aria-hidden="true" size={18} />
            Move to Active Period
          </MenuItem>
        )}
        {canMoveToPrimary && (
          <MenuItem
            onSelect={() => {
              onMoveToPrimary?.(potId);
            }}
            data-testid="move-to-primary-menu-item"
          >
            <GitMergeIcon aria-hidden="true" size={18} />
            Move to Primary Pot
          </MenuItem>
        )}
        {canExtendDeadline && (
          <MenuItem
            onSelect={() => {
              onExtendDeadline?.(potId);
            }}
            data-testid="extend-deadline-action"
          >
            <CalendarClockIcon aria-hidden="true" size={18} />
            Extend submit deadline
          </MenuItem>
        )}
      </MenuContent>
    </Menu>
  );
}
